import { useCallback, useContext, ReactNode, useEffect } from 'react'
import { format } from 'date-fns'
import { Formik, type FormikProps } from 'formik'
import { useShallow } from 'zustand/react/shallow'
import { array, boolean, InferType, mixed, object, string } from 'yup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserClock } from '@fortawesome/pro-regular-svg-icons'

import BrandingContext from '../../../context/BrandingContext'
import useSaveBuildManual from '../../../hooks/useSaveBuildManual'
import useNewUpload, {
  getStateFromAnalysedFile,
  type TNewUploadAnalysedFile,
  type TNewUploadFile,
  type TNewUploadManualFileOptions,
  type TNewUploadManualTuneRequired,
  type TNewUploadOriginalUpload,
  type TUploadType,
} from '../../../hooks/useNewUpload'
import InputGroup from '../../InputGroup'
import RadioGroup from '../../RadioGroup'
import Textarea from '../../Textarea'
import { PrivateAPI } from '../../../utils/api'
import { toCapitalCase } from '../../../utils/strings'
import NewUploadTimeWarning from './FormTimeWarning'
import BuildManualFileOptions from './BuildManualFileOptions'
import BuildManualFiles from './BuildManualFiles'
import BuildEgtDisclaimer from './BuildEgtDisclaimer'
import BuildManualTune from './BuildManualTune'

type Props = {
  analysedFile: TNewUploadAnalysedFile
  formId: string
  manualRequested?: boolean
  onRequestAuto?: () => void
  onSubmitSuccess: () => void
}

type TBuildManualFormProps = {
  formId: string
  formik: FormikProps<TBuildManualValues>
  mainFile: TNewUploadFile
  manualRequested?: boolean
  onRequestAuto?: () => void
  submitError?: string
}

const INITIAL_FILE_OPTIONS = {
  dpf: false,
  dpfPCodes: '',
  egr: false,
  egrPCodes: '',
  o2: false,
  o2PCodes: '',
  ppf: false,
  ppfPCodes: '',
  swirlFlap: false,
  swirlFlapPCodes: '',
  hardcut: false,
  hardcutRpm: '',
  adblue: false,
  adbluePCodes: '',
  egt: false,
  egtPCodes: '',
  vmax: false,
  revLimiter: false,
  immoOff: false,
  popsAndBangs: false,
  staticRevLimiterRaised: false,
  deepIdle: false,
  smokeMap: false,
} satisfies TNewUploadManualFileOptions

const buildManualSchema = object({
  files: array(mixed<TNewUploadFile>().required())
    .default([])
    .test('files-valid', 'Remove invalid files before continuing', (files) =>
      (files || []).every((file) => {
        return Boolean(file?.isAccepted && !file.isRejected && !file.uploadErr)
      }),
    ),
  uploadType: string<TUploadType>().oneOf(['FULL', 'ADJUST']).required(),
  originalUpload: mixed<TNewUploadOriginalUpload | null>().when('uploadType', {
    is: 'ADJUST',
    then: (schema) =>
      schema.test(
        'original-upload-required',
        'Select an original upload',
        (value) => Boolean(value),
      ),
    otherwise: (schema) => schema.nullable().default(null),
  }),
  tuneRequired: string<TNewUploadManualTuneRequired>()
    .oneOf(['STOCK', 'ECO', 'BLEND', 'STAGE1', 'STAGE2', 'STAGE3'])
    .required('Select the tune required'),
  tuneRequiredCustom: string().trim().default(''),
  fileOptions: object({
    dpf: boolean().required().default(false),
    dpfPCodes: string().trim().default(''),
    egr: boolean().required().default(false),
    egrPCodes: string().trim().default(''),
    o2: boolean().required().default(false),
    o2PCodes: string().trim().default(''),
    ppf: boolean().required().default(false),
    ppfPCodes: string().trim().default(''),
    swirlFlap: boolean().required().default(false),
    swirlFlapPCodes: string().trim().default(''),
    hardcut: boolean().required().default(false),
    hardcutRpm: string().when('hardcut', {
      is: true,
      then: (schema) =>
        schema.trim().required('Enter the RPM limiter when driving'),
      otherwise: (schema) => schema.trim().default(''),
    }),
    adblue: boolean().required().default(false),
    adbluePCodes: string().trim().default(''),
    egt: boolean().required().default(false),
    egtPCodes: string().trim().default(''),
    vmax: boolean().required().default(false),
    revLimiter: boolean().required().default(false),
    immoOff: boolean().required().default(false),
    popsAndBangs: boolean().required().default(false),
    staticRevLimiterRaised: boolean().required().default(false),
    deepIdle: boolean().required().default(false),
    smokeMap: boolean().required().default(false),
  }).required(),
}).required()

type TBuildManualValues = InferType<typeof buildManualSchema>

type TValidatedBuildManualValues = TBuildManualValues & {
  files: TNewUploadFile[]
  originalUpload: TNewUploadOriginalUpload | null
  tuneRequired: TNewUploadManualTuneRequired
  tuneRequiredCustom: string
  uploadType: TUploadType
  fileOptions: TNewUploadManualFileOptions
}

type TOriginalUploadApiResult = TNewUploadOriginalUpload & {
  _id?: string
}

const formatOriginalUploadOption = (upload: TOriginalUploadApiResult) => {
  const primaryVehicle = upload.vehicle || upload.manualVehicle
  const vehicleLabel = [primaryVehicle?.make, primaryVehicle?.model]
    .filter(Boolean)
    .join(' ')
  const id = upload.id || upload._id || ''

  return {
    ...upload,
    id,
    label: `#${upload.uploadNumber} - ${toCapitalCase(upload.vehicleType)}${
      vehicleLabel ? ` - ${vehicleLabel}` : ''
    }`,
    value: id,
  }
}

const UploadOption = ({
  innerProps,
  data,
}: {
  innerProps: Record<string, unknown>
  data: TNewUploadOriginalUpload & { label: string }
}) => {
  return (
    <div {...innerProps} className="react-select__option">
      <span className="react-select__option-title">{data.label}</span>
      <span className="react-select__option-subtitle">
        {data.vrm || 'No VRM'} - {data.reference || 'No Reference'}
      </span>
      <span className="react-select__option-meta">
        Uploaded {format(new Date(data.createdAt), "dd MMM yyyy 'at' HH:mm")}
      </span>
    </div>
  )
}

const BuildManualForm = (props: TBuildManualFormProps) => {
  const {
    formId,
    formik,
    mainFile,
    manualRequested,
    onRequestAuto,
    submitError,
  } = props
  const [branding] = useContext(BrandingContext)
  const { primaryColorPalette } = branding
  const { build, isNexusAvailable, setState, vehicle } = useNewUpload(
    useShallow((state) => ({
      build: state.build,
      isNexusAvailable: state.isNexusAvailable,
      setState: state.setState,
      vehicle: state.vehicle,
    })),
  )

  const setManualFileOptions = useCallback(
    (updates: Partial<TNewUploadManualFileOptions>) => {
      formik.setFieldValue(
        'fileOptions',
        {
          ...formik.values.fileOptions,
          ...updates,
        },
        false,
      )
    },
    [formik],
  )

  const setFiles = useCallback(
    (files: TNewUploadFile[]) => {
      formik.setFieldValue('files', files, false)
    },
    [formik],
  )

  const setTuneRequired = useCallback(
    (value: TNewUploadManualTuneRequired) => {
      formik.setFieldValue('tuneRequired', value, false)
      formik.setFieldError('tuneRequired', undefined)
    },
    [formik],
  )

  const setUploadType = useCallback(
    (value: TUploadType) => {
      formik.setFieldValue('uploadType', value, false)
      formik.setFieldError('uploadType', undefined)

      if (value === 'FULL') {
        formik.setFieldValue('originalUpload', null, false)
        formik.setFieldError('originalUpload', undefined)
      }
    },
    [formik],
  )

  const fetchFullUploads = useCallback(async (search?: string) => {
    const response = await PrivateAPI<{
      uploads: TOriginalUploadApiResult[]
    }>({
      url: '/uploads/full',
      method: 'get',
      params: {
        search: search || undefined,
      },
    })

    return response.data.uploads.map((upload) =>
      formatOriginalUploadOption(upload),
    )
  }, [])

  const fileError = formik.values.files.find((file) => {
    return file.rejectReason || file.uploadErr
  })
  const filesValidationError =
    typeof formik.errors.files === 'string' ? formik.errors.files : null

  const originalUploadError: ReactNode =
    typeof formik.errors.originalUpload === 'string'
      ? formik.errors.originalUpload
      : null

  const tuneRequiredError =
    typeof formik.errors.tuneRequired === 'string'
      ? formik.errors.tuneRequired
      : null
  const hardcutRpmError =
    typeof formik.errors.fileOptions?.hardcutRpm === 'string'
      ? formik.errors.fileOptions.hardcutRpm
      : null

  const manualFileOptions = {
    ...INITIAL_FILE_OPTIONS,
    ...formik.values.fileOptions,
  }

  useEffect(() => {
    const nextDisabled =
      formik.values.fileOptions.egt && !build.egtDisclaimerAgreed

    if (build.nextDisabled === nextDisabled) {
      return
    }

    setState({
      build: {
        ...build,
        nextDisabled,
      },
    })
  }, [build, formik.values.fileOptions.egt, setState])

  return (
    <>
      <form id={formId} onSubmit={formik.handleSubmit}>
        {(manualRequested || isNexusAvailable) && (
          <div className="flex items-center justify-center border-b border-gray-200 px-6 py-2.5">
            <div>
              <FontAwesomeIcon
                icon={faUserClock}
                className="text-pink-800 text-sm"
              />
            </div>
            <p className="ml-2 text-sm leading-5 text-gray-700">
              {manualRequested ? (
                <>
                  You have requested a manual build.{' '}
                  <button
                    className={`font-medium cursor-pointer underline text-${primaryColorPalette}-600 hover:text-${primaryColorPalette}-700`}
                    type="button"
                    onClick={onRequestAuto}
                  >
                    Click here to return to an auto build.
                  </button>
                </>
              ) : (
                'No matches found. Complete the form below for a manual build.'
              )}
            </p>
          </div>
        )}
        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-5">
          <BuildManualFiles
            mainFile={mainFile}
            files={formik.values.files}
            filesValidationError={filesValidationError}
            fileError={fileError}
            setFiles={setFiles}
            submitError={submitError}
          />
          <div className="col-span-5 space-y-4 p-6 sm:col-span-3">
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium leading-5 text-black">
                  Upload type
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Adjustment remaps must be linked to one of your previous full
                  remaps.
                </p>
              </div>
              <RadioGroup
                Container={({ children }: { children: ReactNode }) => (
                  <div className="space-y-2">{children}</div>
                )}
                selected={formik.values.uploadType}
                setSelected={(value: string) =>
                  setUploadType(value as TUploadType)
                }
                items={[
                  {
                    label: 'Full remap',
                    value: 'FULL',
                    description:
                      'This is the first time you are remapping this vehicle.',
                  },
                  {
                    label: 'Adjustment remap',
                    value: 'ADJUST',
                    description: 'You need an adjustment to an existing remap.',
                  },
                ]}
              />
              {formik.values.uploadType === 'ADJUST' ? (
                <InputGroup
                  noFormik
                  required
                  type="async-select"
                  label="Original upload"
                  id="original-upload"
                  error={originalUploadError}
                  onChange={(value) => {
                    formik.setFieldValue(
                      'originalUpload',
                      (value || null) as TNewUploadOriginalUpload | null,
                      false,
                    )
                    formik.setFieldError('originalUpload', undefined)
                  }}
                  value={
                    formik.values.originalUpload
                      ? formatOriginalUploadOption(formik.values.originalUpload)
                      : null
                  }
                  isClearable
                  placeholder="Select an upload"
                  loadOptions={fetchFullUploads}
                  components={{ Option: UploadOption }}
                />
              ) : null}
            </div>
            <BuildManualTune
              fuel={vehicle.fuel}
              selected={formik.values.tuneRequired}
              setSelected={setTuneRequired}
              tuneRequiredError={tuneRequiredError}
            />
            <div>
              <Textarea
                noFormik
                id="tuneRequiredCustom"
                label="Details on tune required"
                minRows={6}
                placeholder="Provide details of tune required including hardware modifications and any other requirements we should know about."
                value={formik.values.tuneRequiredCustom}
                onChange={(event) => {
                  void formik.setFieldValue(
                    'tuneRequiredCustom',
                    event.currentTarget.value,
                    false,
                  )
                }}
              />
            </div>
            <BuildManualFileOptions
              fuel={vehicle.fuel}
              hardcutRpmError={hardcutRpmError}
              manualFileOptions={manualFileOptions}
              setManualFileOptions={setManualFileOptions}
            />
            {formik.values.fileOptions.egt && (
              <BuildEgtDisclaimer
                checked={build.egtDisclaimerAgreed}
                onChange={() =>
                  setState({
                    build: {
                      ...build,
                      egtDisclaimerAgreed: !build.egtDisclaimerAgreed,
                    },
                  })
                }
              />
            )}
            <NewUploadTimeWarning />
          </div>
        </div>
      </form>
    </>
  )
}

const BuildManual = (props: Props) => {
  const {
    analysedFile,
    formId,
    manualRequested,
    onRequestAuto,
    onSubmitSuccess,
  } = props
  const saveBuildManualMutation = useSaveBuildManual()
  const { build, manual, setState } = useNewUpload(
    useShallow((state) => ({
      build: state.build,
      manual: state.manual,
      setState: state.setState,
    })),
  )

  const initialValues: TBuildManualValues = {
    files: manual.files,
    originalUpload: manual.originalUpload,
    tuneRequired: manual.tuneRequired,
    tuneRequiredCustom: manual.tuneRequiredCustom,
    uploadType: manual.uploadType,
    fileOptions: {
      ...INITIAL_FILE_OPTIONS,
      ...manual.fileOptions,
    },
  }

  const mainFile: TNewUploadFile = {
    id: analysedFile.file.id,
    name: analysedFile.file.name,
    size: parseInt(`${analysedFile.file.size}`, 10) || 0,
    fileBlob: null,
    isUploading: false,
    uploadProgress: 100,
    isUploaded: true,
    isAccepted: true,
    isRejected: false,
    rejectReason: null,
    uploadErr: null,
    uploadUrl: null,
    isPreparing: false,
  }

  const handleSubmit = useCallback(
    async (values: TBuildManualValues) => {
      const validatedValues = values as TValidatedBuildManualValues

      setState({
        build: {
          ...build,
          isSaving: true,
        },
      })

      try {
        const saveResponse = await saveBuildManualMutation.mutateAsync({
          analysedFileId: analysedFile.id,
          files: validatedValues.files,
          originalUpload: validatedValues.originalUpload,
          tuneRequired: validatedValues.tuneRequired,
          tuneRequiredCustom: validatedValues.tuneRequiredCustom,
          uploadType: validatedValues.uploadType,
          fileOptions: validatedValues.fileOptions,
        })

        setState(getStateFromAnalysedFile(saveResponse.analysedFile))
        onSubmitSuccess()
      } catch (err) {
        setState({
          build: {
            ...build,
            isSaving: false,
          },
        })

        throw err
      }
    },
    [
      analysedFile.id,
      build,
      onSubmitSuccess,
      saveBuildManualMutation,
      setState,
    ],
  )

  return (
    <Formik<TBuildManualValues>
      initialValues={initialValues}
      enableReinitialize
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={buildManualSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <BuildManualForm
          formId={formId}
          formik={formik}
          mainFile={mainFile}
          manualRequested={manualRequested}
          onRequestAuto={onRequestAuto}
          submitError={saveBuildManualMutation.error?.message}
        />
      )}
    </Formik>
  )
}

export default BuildManual
